// Unified helper for showing OS-level notifications.
//
// Inside Electron we route through the main process so macOS/Windows attribute
// the notification to the app identity (icon + bundle name). Using `new
// Notification(...)` from a renderer — even inside Electron — makes macOS
// render a "localhost:5173" origin line under the title.
//
// Outside Electron (regular browser), we fall back to the Web Notifications
// API; the origin line is unavoidable there but at least behaviour is the same.

import type { ElectronNotificationClick } from '@/hooks/useElectron';

export interface NativeNotificationOptions {
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: Record<string, unknown>;
  onClick?: () => void;
}

type ClickHandler = (payload: ElectronNotificationClick) => void;

const electronClickHandlers = new Map<string, () => void>();
let unsubscribeElectronListener: (() => void) | null = null;

function ensureElectronClickListener() {
  if (unsubscribeElectronListener) return;
  if (typeof window === 'undefined' || !window.electronAPI?.onNotificationClicked) return;

  const handler: ClickHandler = (payload) => {
    const tag = payload?.tag;
    if (!tag) return;
    const cb = electronClickHandlers.get(tag);
    if (cb) cb();
  };
  unsubscribeElectronListener = window.electronAPI.onNotificationClicked(handler);
}

export async function showNativeNotification(
  title: string,
  options: NativeNotificationOptions = {}
): Promise<void> {
  const electronAPI = typeof window !== 'undefined' ? window.electronAPI : undefined;

  if (electronAPI?.showNotification) {
    try {
      if (options.onClick && options.tag) {
        ensureElectronClickListener();
        electronClickHandlers.set(options.tag, options.onClick);
      }
      const result = await electronAPI.showNotification({
        title,
        body: options.body,
        tag: options.tag,
        data: options.data,
        silent: options.silent,
      });
      if (result?.success) return;
    } catch {
      // fall through to web Notification
    }
  }

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  const n = new Notification(title, {
    body: options.body,
    icon: options.icon,
    badge: options.badge,
    tag: options.tag,
    requireInteraction: options.requireInteraction,
    silent: options.silent,
    data: options.data,
  });
  if (options.onClick) {
    n.onclick = () => {
      options.onClick?.();
      n.close();
    };
  }
}
